/**
 * Live estimate → what the monitoring step prints.
 *
 * The backend owns the cost so far, the charge rate and the time left
 * (`greencharge.live-estimate.v1`; sw-ev-api docs/SESSION_LIVE_ESTIMATE.md).
 * This module only chooses between the backend's own `display` string,
 * "pending" (the screen shows its animated "Calculating"), "Paused" and "—".
 * There is no arithmetic on money, power or time here, and there must never
 * be: a missing or unreadable estimate is pending, not a local calculation.
 */

export const LIVE_ESTIMATE_CONTRACT = 'greencharge.live-estimate.v1';

export const DASH = '—';
export const PAUSED_TEXT = 'Paused';

const pending = () => ({ pending: true, text: null });
const text = (value) => ({ pending: false, text: value });
const displayOf = (part) => (typeof part?.display === 'string' && part.display.trim() ? part.display : null);
const isObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

/** An object we can read as a live estimate (any v1 revision). */
export function isLiveEstimate(value) {
  return isObject(value) && typeof value.contract === 'string' && value.contract.startsWith('greencharge.live-estimate.');
}

/** Millisecond timestamp of `asOf`, or null when it cannot be read. */
export function liveEstimateAsOfMs(estimate) {
  const ms = Date.parse(estimate?.asOf);
  return Number.isFinite(ms) ? ms : null;
}

// Cost: only ever the backend's string. A known status without a string
// (or an unknown status) is pending — never 0.00.
function costView(cost) {
  const status = cost?.status;
  if (status === 'ESTIMATED' || status === 'FINAL' || status === 'NO_CHARGE') {
    const display = displayOf(cost);
    return display ? text(display) : pending();
  }
  return pending();
}

function chargeRateView(rate, { clientPaused }) {
  const status = rate?.status;
  if (status === 'NOT_APPLICABLE') return text(DASH);
  // The screen already says "Charging paused": no rate under that heading,
  // even if the estimate has not caught up with the pause yet.
  if (status === 'PAUSED' || clientPaused) return text(PAUSED_TEXT);
  if (status === 'MEASURED') {
    const display = displayOf(rate);
    return display ? text(display) : pending();
  }
  return pending();
}

function timeRemainingView(time, { clientPaused, perMinute }) {
  const status = time?.status;
  if (status === 'NOT_APPLICABLE' || status === 'PAUSED') return text(DASH);
  if (status === 'REACHED') return text(displayOf(time) || '0:00');
  // A per-minute time left keeps counting through a pause (the tariff bills
  // by the minute); an energy-based one means nothing while no power flows.
  const timeBased = time?.basis ? time.basis === 'BUDGET_TIME' : perMinute;
  if (clientPaused && !timeBased) return text(DASH);
  if (status === 'ESTIMATED') {
    const display = displayOf(time);
    return display ? text(display) : pending();
  }
  return pending();
}

/**
 * @param {object|null|undefined} estimate - `liveEstimate` as received, or nothing.
 * @param {object} [options]
 * @param {boolean} [options.clientPaused] - the screen's own presentation says paused.
 * @param {'kwh'|'minute'} [options.fallbackBillingUnit] - the session's stored
 *   billing unit, used only until an estimate arrives.
 * @returns {{
 *   hasEstimate: boolean, isPending: boolean, phase: string|null,
 *   billingUnit: 'kwh'|'minute',
 *   cost: {pending: boolean, text: string|null}, costStatus: string|null, noCharge: boolean,
 *   chargeRate: {pending: boolean, text: string|null},
 *   timeRemaining: {pending: boolean, text: string|null}, timeRemainingSeconds: number|null,
 *   progressPercent: number|null, budgetReached: boolean,
 * }}
 */
export function describeLiveEstimate(estimate, { clientPaused = false, fallbackBillingUnit = 'kwh' } = {}) {
  const live = isLiveEstimate(estimate) ? estimate : null;
  const billingUnit = live?.billingUnit === 'minute' || live?.billingUnit === 'kwh'
    ? live.billingUnit
    : (fallbackBillingUnit === 'minute' ? 'minute' : 'kwh');
  const perMinute = billingUnit === 'minute';

  const cost = costView(live?.cost);
  const budget = isObject(live?.budget) ? live.budget : null;
  const rawPercent = budget?.progressPercent;
  const progressPercent = typeof rawPercent === 'number' && Number.isFinite(rawPercent)
    ? Math.min(100, Math.max(0, rawPercent))
    : null;
  const seconds = live?.timeRemaining?.status === 'ESTIMATED' ? live.timeRemaining.seconds : null;

  return {
    hasEstimate: !!live,
    isPending: !live || cost.pending,
    phase: typeof live?.phase === 'string' ? live.phase : null,
    billingUnit,
    cost,
    costStatus: typeof live?.cost?.status === 'string' ? live.cost.status : null,
    noCharge: live?.cost?.status === 'NO_CHARGE',
    chargeRate: chargeRateView(live?.chargeRate, { clientPaused }),
    timeRemaining: timeRemainingView(live?.timeRemaining, { clientPaused, perMinute }),
    timeRemainingSeconds: typeof seconds === 'number' && Number.isFinite(seconds) ? seconds : null,
    progressPercent,
    budgetReached: budget?.reached === true,
  };
}

/**
 * Per-minute "Budget almost reached": the last fifth of the budget, or the
 * last 30 seconds — both read from the estimate, not worked out here.
 */
export function isBudgetAlmostReached(view) {
  if (!view || view.billingUnit !== 'minute' || view.budgetReached) return false;
  const seconds = view.timeRemainingSeconds;
  if (seconds == null || seconds <= 0) return false;
  return (view.progressPercent != null && view.progressPercent >= 80) || seconds <= 30;
}

/** The first sentence of that banner: whole minutes left, as the screen has always worded it. */
export function budgetAlmostReachedLead(view) {
  const minutes = Math.ceil((view?.timeRemainingSeconds ?? 0) / 60);
  return (view?.timeRemainingSeconds ?? 0) >= 60
    ? `Less than ${minutes} minute${minutes !== 1 ? 's' : ''} remaining.`
    : 'Less than a minute remaining.';
}
